// The press helper.
//
// WHY THIS IS NOT onClick
// -----------------------
// On touch, a synthesised `click` is withheld whenever the browser is still
// deciding whether the gesture belongs to something else — a scroll, a pan, a
// double-tap-to-zoom. Inside this game the felt claims the pointer stream and
// `touch-action: none` suppresses the rest, so `click` frequently never
// arrives. The symptoms were all the same missing click: SPIN doing nothing on
// the first press, the back button dead, a settings toggle that needed two
// taps.
//
// The lesson from that round, recorded here because it cost a round-trip: when
// this class of bug is found, sweep EVERY control of the same class in the same
// change. Fixing only the two the owner happened to notice left five identical
// ones broken.
//
// press() binds down/up on the element itself and fires only when both happened
// on THAT element with the same pointer, so a press that starts elsewhere and
// drifts over can never trigger it. stopPropagation keeps the board's own
// pointer pipeline out of it.
//
// INVARIANT: `grep -c onClick` inside this folder stays 0.

/** Which pointer armed which element. Weak so a removed node cannot leak. */
const armed = new WeakMap<Element, number>();

export function press(fn: () => void) {
  return {
    onPointerDown: (e: React.PointerEvent) => {
      e.stopPropagation();
      armed.set(e.currentTarget, e.pointerId);
    },
    onPointerUp: (e: React.PointerEvent) => {
      e.stopPropagation();
      if (armed.get(e.currentTarget) !== e.pointerId) return;
      armed.delete(e.currentTarget);
      /* The Pointer Events spec does not exempt disabled controls the way
         click does, and Chromium has shipped both behaviours. Trusting the
         disabled attribute here makes the guard browser-independent.
         (Recheck P10.) */
      if ((e.currentTarget as HTMLButtonElement).disabled) return;
      fn();
    },
    onPointerCancel: (e: React.PointerEvent) => { armed.delete(e.currentTarget); },
    onLostPointerCapture: (e: React.PointerEvent) => { armed.delete(e.currentTarget); },
    /* KEYBOARD, in the one place it covers everything. Because no element in
       this folder carries onClick (the invariant this module exists for), a
       native button's Enter-fires-click did NOTHING -- every control was a
       keyboard dead end (2026-08-16 audit, m7). Handling it here makes every
       press()-driven control operable at once: buttons natively focusable,
       role=button hosts via their tabIndex. keydown, not keyup, so there is no
       double fire -- nothing else listens for keys on these elements. */
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      if (e.repeat) return;                 // a held key is one press, not many
      if ((e.currentTarget as HTMLButtonElement).disabled) return;
      e.preventDefault();
      e.stopPropagation();
      fn();
    },
  };
}

export default press;
